"use client";

import { motion } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";

function useIsTouchDevice() {
  const [isTouch, setIsTouch] = useState(false);
  useEffect(() => {
    setIsTouch("ontouchstart" in window || navigator.maxTouchPoints > 0);
  }, []);
  return isTouch;
}

function ReelCard({ video }: { video: { url: string; title: string } }) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [playing, setPlaying] = useState(false);
    const [muted, setMuted] = useState(true);
    const isTouch = useIsTouchDevice();

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handlePlay = () => {
            setPlaying(true);
            const allVideos = document.querySelectorAll('video');
            allVideos.forEach(v => {
                if (v !== video && !v.paused) {
                    v.pause();
                }
            });
        };

        const handlePause = () => setPlaying(false);

        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);

        return () => {
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
        };
    }, []);

    const toggleMute = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
            setMuted((m) => !m);
        }
    };

    const handlePlayToggle = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!videoRef.current) return;
        if (videoRef.current.paused) {
            videoRef.current.play();
            setPlaying(true);
        } else {
            videoRef.current.pause();
            setPlaying(false);
        }
    };

    const handleMouseEnter = () => {
        if (isTouch) return;
        videoRef.current?.play();
        setPlaying(true);
    };

    const handleMouseLeave = () => {
        if (isTouch) return;
        videoRef.current?.pause();
        setPlaying(false);
    };

    return (
        <div
            className="flex-none w-[50%] xs:w-[45%] sm:w-[35%] md:w-[28%] lg:w-[22%] xl:w-[16%] aspect-[9/16] relative rounded-2xl overflow-hidden snap-start bg-[#130724] border border-white/10 shadow-2xl transition-transform duration-500 hover:scale-[1.02] cursor-pointer group"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handlePlayToggle}
        >
            <video
                ref={videoRef}
                src={video.url}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                muted
                loop
                playsInline
                preload="metadata"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent z-10 pointer-events-none" />

            {/* Mute Toggle */}
            <button
                onClick={toggleMute}
                aria-label={muted ? "Unmute" : "Mute"}
                className="absolute top-4 right-4 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-black/60 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white active:scale-90 transition-transform z-30 pointer-events-auto"
            >
                {muted ? <VolumeX className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <Volume2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
            </button>

            {/* Play/Pause Button */}
            <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
                <button
                    aria-label={playing ? "Pause" : "Play"}
                    onClick={handlePlayToggle}
                    className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/20 backdrop-blur-sm border border-white/40 flex items-center justify-center transition-all duration-300 active:scale-90 pointer-events-auto
                        ${playing ? "opacity-0 scale-90" : "opacity-100 scale-100"}`}
                >
                    {playing
                        ? <Pause className="w-5 h-5 text-white fill-white" />
                        : <Play className="w-5 h-5 text-white fill-white ml-1" />
                    }
                </button>
            </div>

            <div className="absolute bottom-6 left-6 right-6 z-20 pointer-events-none">
                <h3 className="text-white font-semibold text-lg md:text-xl tracking-tight capitalize">{video.title}</h3>
            </div>
        </div>
    );
}

export default function SimpleBooking({ videos = [] }: { videos?: any[] }) {
    const steps = [
        {
            title: "Step 1",
            subtitle: "Wedding events",
            desc: "Elegant musical experiences for weddings, sangeet nights, receptions, and destination celebrations.",
            finalColor: "#d82894",
            gradient: ["#6a00ff", "#d82894"],
        },
        {
            title: "Step 2",
            subtitle: "Consultation & Music Planning",
            desc: "Discuss audience preferences, themes, and performance expectations.",
            finalColor: "#0073FF",
            gradient: ["#d82894", "#0073FF"],
        },
        {
            title: "Step 3",
            subtitle: "Event Preparation",
            desc: "Customized playlist planning and performance coordination.",
            finalColor: "#a855f7",
            gradient: ["#0073FF", "#a855f7"],
        },
        {
            title: "Step 4",
            subtitle: "Live Performance Experience",
            desc: "An unforgettable entertainment experience delivered with energy and professionalism.",
            finalColor: "#d82894",
            gradient: ["#a855f7", "#ff0080"],
        }
    ];

    const categories = ["All", ...Array.from(new Set(videos.map(v => v.category).filter(Boolean)))];
    const [activeCategory, setActiveCategory] = useState("All");

    const filteredVideos = activeCategory === "All"
        ? videos
        : videos.filter(v => v.category === activeCategory);

    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    const handleScroll = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            setCanScrollLeft(scrollLeft > 10);
            // 10px buffer to account for rounding errors
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };

    useEffect(() => {
        handleScroll();
        window.addEventListener("resize", handleScroll);
        return () => window.removeEventListener("resize", handleScroll);
    }, []);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo({ left: 0, behavior: "smooth" });
        }
        // Small delay to allow DOM to render new items before checking scroll
        const timeout = setTimeout(handleScroll, 50);
        return () => clearTimeout(timeout);
    }, [activeCategory, filteredVideos.length]);

    const scroll = (direction: "left" | "right") => {
        if (scrollRef.current) {
            const { clientWidth } = scrollRef.current;
            const scrollAmount = direction === "left" ? -clientWidth / 2 : clientWidth / 2;
            scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
        }
    };

    return (
        <div className="w-full pb-10 sm:py-[2vw] flex flex-col items-center relative z-10">

            {/* Glow */}
            <div className="absolute left-[-200px] bottom-[1200px] sm:left-[-5vw] sm:bottom-[-14vw] -z-10 h-[300px] w-[300px] sm:h-[25vw] sm:w-[25vw] rounded-full bg-[#0073FF]/20 blur-[130px]"></div>

            <div className="absolute top-[2200px] right-[-200px] sm:right-[7vw] sm:top-[10vw] -z-10 h-[300px] w-[300px] sm:h-[25vw] sm:w-[25vw] rounded-full bg-[#400077]/80 blur-[130px]"></div>

            {/* <h2 className="px-[5%] text-3xl sm:text-[2.8vw] font-semibold bg-linear-to-r from-[#B8BBBF] to-[#0073FF] bg-clip-text text-transparent mb-16 sm:mb-[6vw] text-center">
                Simple Booking. Exceptional Experience
            </h2> */}

            {/* <div className="px-[5%] flex flex-col lg:flex-row items-center justify-center w-full gap-8 lg:gap-0 mx-auto">
                {steps.map((step, index) => (
                    <div key={index} className="relative w-[85%] max-w-[320px] md:max-w-none md:w-[45vw] lg:w-[23vw] aspect-square flex flex-col justify-center items-center text-center p-6 lg:p-[2.5vw] lg:-ml-[2vw] first:ml-0">

                        <div className="absolute inset-[10px] lg:inset-[1vw] rounded-full bg-[#0a0410] shadow-2xl"></div>

                        <svg className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none overflow-visible" viewBox="0 0 100 100">
                            <defs>
                                <linearGradient id={`grad-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor={step.gradient[0]} />
                                    <stop offset="100%" stopColor={step.gradient[1]} />
                                </linearGradient>
                                <filter id={`glow-${index}`} x="-20%" y="-20%" width="140%" height="140%">
                                    <feGaussianBlur stdDeviation="3" result="blur" />
                                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                                </filter>
                            </defs>

                            <circle cx="50" cy="50" r="45" stroke="#130724" strokeWidth="8" fill="none" />

                            <motion.circle
                                cx="50" cy="50" r="45"
                                stroke={`url(#grad-${index})`}
                                strokeWidth="8"
                                fill="none"
                                strokeLinecap="round"
                                initial={{ pathLength: 0, transition: { duration: 0 } }}
                                whileInView={{
                                    pathLength: 1,
                                    transition: { duration: 1.5, delay: index * 0.4, ease: "easeInOut" }
                                }}
                                viewport={{ once: false, margin: "-50px" }}
                                filter={`url(#glow-${index})`}
                            />
                        </svg>

                        <div className="relative z-20 flex flex-col items-center justify-center h-full max-w-[85%]">
                            <motion.h3
                                initial={{ color: "#6b7280", transition: { duration: 0 } }}
                                whileInView={{
                                    color: step.finalColor,
                                    transition: { duration: 1.5, delay: index * 0.4, ease: "easeInOut" }
                                }}
                                viewport={{ once: false, margin: "-50px" }}
                                className="text-2xl lg:text-[1.8vw] font-bold mb-2 lg:mb-[0.5vw]"
                            >
                                {step.title}
                            </motion.h3>
                            <h4 className="text-[#D946EF] font-medium text-sm lg:text-[1.2vw] mb-3 lg:mb-[1vw] leading-tight">
                                {step.subtitle}
                            </h4>
                            <p className="text-[#E2E8F0] text-xs lg:text-[1.1vw] leading-snug">
                                {step.desc}
                            </p>
                        </div>
                    </div>
                ))}
            </div> */}

            {/* Video Section Header */}
            <div className="w-full flex flex-col items-center mt-12 sm:mt-[1vw] px-[5%] text-center relative z-20">
                <h2 className="text-3xl sm:text-[2.8vw] font-semibold bg-linear-to-r from-[#D946EF] to-[#0073FF] bg-clip-text text-transparent mb-4 sm:mb-[1vw]">
                    Experience The Magic Live
                </h2>
                <p className="text-[#E2E8F0] text-sm sm:text-base md:text-[1.1vw] leading-relaxed">
                    Browse through unforgettable moments, high-energy stage performances, and soulful musical highlights.
                </p>
            </div>

            {/* Category Tabs */}
            {categories.length > 1 && (
                <div 
                    className="flex sm:flex-wrap overflow-x-auto sm:overflow-x-visible justify-start sm:justify-center gap-2 sm:gap-3 mt-12 sm:mt-[4vw] px-[5%] z-20 relative w-full pb-2 sm:pb-0 [&::-webkit-scrollbar]:hidden"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                    {categories.map((category, idx) => {
                        const isActive = activeCategory === category;
                        return (
                            <motion.button
                                key={idx}
                                onClick={() => setActiveCategory(category)}
                                className={`relative overflow-hidden px-4 py-1.5 sm:px-6 sm:py-2 rounded-xl text-xs sm:text-base font-medium transition-colors duration-300 uppercase cursor-pointer border shrink-0 
                                ${isActive
                                        ? "border-[#8a2be2] text-white shadow-[0_0_20px_rgba(138,43,226,0.4)]"
                                        : "border-[#4c1e7e] text-[#e2e2e2] hover:text-white hover:border-[#8a2be2]"
                                    }`}
                                initial="initial"
                                animate={isActive ? "active" : "initial"}
                                whileHover={isActive ? "active" : "hover"}
                            >
                                {/* Base Background */}
                                <div className="absolute inset-0 bg-linear-to-br from-[#400077] via-black to-[#0a0410] z-0" />

                                {/* Liquid Hover/Active Background */}
                                <motion.div
                                    className="absolute inset-0 bg-linear-to-r from-[#400077] via-[#9d4edd] to-[#400077] z-0"
                                    style={{ backgroundSize: "200% 200%" }}
                                    variants={{
                                        initial: { opacity: 0, backgroundPosition: "0% 50%" },
                                        hover: {
                                            opacity: 1,
                                            backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                                            transition: {
                                                opacity: { duration: 0.3 },
                                                backgroundPosition: { duration: 3, ease: "linear", repeat: Infinity }
                                            }
                                        },
                                        active: {
                                            opacity: 1,
                                            backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                                            transition: {
                                                opacity: { duration: 0.3 },
                                                backgroundPosition: { duration: 3, ease: "linear", repeat: Infinity }
                                            }
                                        }
                                    }}
                                />

                                <span className="relative z-10">{category}</span>
                            </motion.button>
                        );
                    })}
                </div>
            )}

            {/* Apple-style Video Carousel */}
            <div className="w-full relative mt-8 sm:mt-[3vw]">
                {/* Left Arrow */}
                {canScrollLeft && (
                    <button
                        onClick={() => scroll("left")}
                        className="absolute left-[2%] top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-md border border-white/10 transition-all text-white hidden md:flex items-center justify-center shadow-xl cursor-pointer"
                        aria-label="Scroll left"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
                    </button>
                )}

                {/* Right Arrow */}
                {canScrollRight && (
                    <button
                        onClick={() => scroll("right")}
                        className="absolute right-[2%] top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-md border border-white/10 transition-all text-white hidden md:flex items-center justify-center shadow-xl cursor-pointer"
                        aria-label="Scroll right"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                    </button>
                )}

                {/* Track */}
                <div
                    ref={scrollRef}
                    onScroll={handleScroll}
                    className={`flex gap-4 sm:gap-6 overflow-x-auto snap-x snap-mandatory px-[5%] scroll-pl-[5%] py-8 [&::-webkit-scrollbar]:hidden ${
                        !canScrollLeft && !canScrollRight ? "justify-center" : ""
                    }`}
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                    {filteredVideos.length > 0 ? (
                        filteredVideos.map((video, index) => (
                            <ReelCard key={video.id || index} video={video} />
                        ))
                    ) : (
                        <div className="w-full text-center py-10 text-white/60">
                            No videos found for this category.
                        </div>
                    )}
                </div>
            </div>

        </div>
    )
}





